'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';
import type { Translations } from '@/lib/i18n/translations';
import LanguageSwitcher from '@/components/ui/LanguageSwitcher';
import { usePwaInstall } from '@/lib/hooks/usePwaInstall';
import { IosInstallSheet } from '@/components/pwa/InstallCard';
import { inter } from '@/lib/inter-font';

// Same value/labelKey separation already used in dashboard/page.tsx for
// business categories: `value` is the exact string persisted to
// businesses.category (unchanged, never translated) and `labelKey`
// resolves the displayed label from the same t.dashboard.categories
// translation source dashboard uses — kept as its own local array
// (register/page.tsx has never imported from dashboard/page.tsx) but
// deliberately sourced from the same translation keys so the two pages
// can never drift into inconsistent labels for the same stored value.
const CATEGORIES: { value: string; labelKey: keyof Translations['dashboard']['categories'] }[] = [
  { value: 'Salon Bukurie', labelKey: 'beautySalon' },
  { value: 'Berber', labelKey: 'barber' },
  { value: 'Klinikë Dentare', labelKey: 'dentalClinic' },
  { value: 'Klinikë Mjekësore', labelKey: 'medicalClinic' },
  { value: 'Spa & Wellness', labelKey: 'spaWellness' },
  { value: 'Palestër / Fitness', labelKey: 'gymFitness' },
  { value: 'Fotograf', labelKey: 'photographer' },
  { value: 'Konsulent', labelKey: 'consultant' },
  { value: 'Avokat', labelKey: 'lawyer' },
  { value: 'Kontabilist', labelKey: 'accountant' },
  { value: 'Qendër estetike / Aesthetic Center', labelKey: 'aestheticCenter' },
  { value: 'Nail Artist', labelKey: 'nailArtist' },
  { value: 'Tjetër', labelKey: 'other' },
];

function resolveCategoryLabel(value: string, catLabels: Translations['dashboard']['categories']): string {
  const entry = CATEGORIES.find((c) => c.value === value);
  return entry ? catLabels[entry.labelKey] : value;
}

// Display order only — the stored schedule structure keys off these same
// day-of-week strings, unchanged. Labels are resolved at render time from
// t.dashboard.days (the same weekday translations the dashboard's own
// schedule UI already uses), never hardcoded here.
const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

interface DaySchedule {
  is_open: boolean;
  open_time: string;
  close_time: string;
}

const defaultSchedule = (): Record<string, DaySchedule> => ({
  monday:    { is_open: true,  open_time: '09:00', close_time: '18:00' },
  tuesday:   { is_open: true,  open_time: '09:00', close_time: '18:00' },
  wednesday: { is_open: true,  open_time: '09:00', close_time: '18:00' },
  thursday:  { is_open: true,  open_time: '09:00', close_time: '18:00' },
  friday:    { is_open: true,  open_time: '09:00', close_time: '18:00' },
  saturday:  { is_open: true,  open_time: '09:00', close_time: '14:00' },
  sunday:    { is_open: false, open_time: '09:00', close_time: '18:00' },
});

// Maps register_business's controlled RAISE EXCEPTION messages to
// user-facing, language-aware text — never shows raw Postgres error
// text. Internal error codes (the map's keys) are unchanged; only the
// displayed message now follows the selected language. Anything
// unrecognized falls back to the same generic message this form has
// always shown.
function mapRegisterError(message: string | undefined, errors: Translations['register']['errors']): string {
  const map: Record<string, string> = {
    authentication_required: errors.authenticationRequired,
    name_required: errors.businessNameRequired,
    business_already_exists: errors.businessAlreadyExists,
    slug_generation_failed: errors.slugGenerationFailed,
  };
  if (message && map[message]) return map[message];
  return errors.generic;
}

export default function RegisterPage() {
  const router = useRouter();
  const { signUp } = useAuth();
  const { t } = useLanguage();
  const tr = t.register;
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [form, setForm] = useState({ businessName: '', email: '', password: '', phone: '' });
  const [onboarding, setOnboarding] = useState({ category: '', address: '', city: '', description: '' });
  const [schedule, setSchedule] = useState<Record<string, DaySchedule>>(defaultSchedule());
  const [bookingInterval, setBookingInterval] = useState<15 | 30 | 60>(30);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [createdBusinessId, setCreatedBusinessId] = useState<string | null>(null);
  // Shown after step 3 succeeds, replacing the 3-step form — registration
  // itself is already fully complete by the time this is set; installing
  // Rezervo from here is purely optional (see the render branch below).
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const { mounted: pwaMounted, isIOS, isAndroid, canInstallAndroid, promptInstall } = usePwaInstall();
  const [showIosSheet, setShowIosSheet] = useState(false);

  const handleStep1 = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.businessName.trim()) { setError(tr.errors.businessNameRequired); return; }
    if (!form.email.trim()) { setError(tr.errors.emailRequired); return; }
    if (form.password.length < 6) { setError(tr.errors.passwordTooShort); return; }
    setLoading(true);
    try {
      await signUp(form.email, form.password, { fullName: form.businessName });
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // Trusted server-side registration: owner_id, slug, trial dates,
        // and subscription_status are all derived/generated inside
        // register_business — this form never computes or supplies any
        // of them. The final assigned slug comes back in the response
        // (row.slug) but isn't needed anywhere else in this flow today,
        // since nothing here displays it and the dashboard re-fetches
        // the business fresh by owner_id after registration completes.
        const { data, error: rpcError } = await supabase.rpc('register_business', {
          p_name: form.businessName,
          p_phone: form.phone || null,
        });
        if (rpcError) throw rpcError;
        const row = Array.isArray(data) ? data[0] : data;
        if (row?.business_id) {
          setCreatedBusinessId(row.business_id);
        }
      }
      setStep(2);
    } catch (err: any) {
      setError(mapRegisterError(err?.message, tr.errors));
    } finally {
      setLoading(false);
    }
  };

  const handleStep2 = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!onboarding.category) { setError(tr.errors.categoryRequired); return; }
    if (!onboarding.city.trim()) { setError(tr.errors.cityRequired); return; }
    setLoading(true);
    try {
      if (createdBusinessId) {
        const supabase = createClient();
        // onboarding.category is always one of CATEGORIES[].value (the
        // <select> below only ever sets it from that list) — the raw,
        // untranslated value, never a display label. Persisted exactly
        // as selected, regardless of UI language.
        const { error: updateErr } = await supabase.from('businesses').update({
          category: onboarding.category,
          address: onboarding.address || null,
          city: onboarding.city,
          description: onboarding.description || null,
        }).eq('id', createdBusinessId);
        if (updateErr) throw updateErr;
      }
      setStep(3);
    } catch (err: any) {
      setError(err.message || tr.errors.generic);
    } finally {
      setLoading(false);
    }
  };

  const handleStep3 = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!createdBusinessId) { setError(tr.errors.businessIdMissing); return; }
    setLoading(true);
    try {
      const supabase = createClient();
      // Save booking interval to business
      const { error: bizErr } = await supabase.from('businesses').update({
        booking_interval_minutes: bookingInterval,
        onboarding_completed: true,
      }).eq('id', createdBusinessId);
      if (bizErr) throw bizErr;

      // Save weekly schedules
      const scheduleRows = DAY_KEYS.map((key) => ({
        business_id: createdBusinessId,
        day_of_week: key,
        open_time: schedule[key].open_time,
        close_time: schedule[key].close_time,
        is_open: schedule[key].is_open,
        slot_duration_minutes: bookingInterval,
      }));
      const { error: schedErr } = await supabase.from('schedules').insert(scheduleRows);
      if (schedErr) throw schedErr;

      // Registration is fully complete at this point — the success
      // screen below only offers an OPTIONAL install step before
      // continuing; it never blocks or delays reaching the dashboard.
      setRegistrationComplete(true);
    } catch (err: any) {
      setError(err.message || tr.errors.generic);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '10px',
    padding: '12px 16px',
    color: '#fff',
    fontSize: '15px',
    outline: 'none',
    boxSizing: 'border-box',
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0a0015 0%, #1a0035 50%, #0d001f 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: inter.style.fontFamily }}>
      <div style={{ width: '100%', maxWidth: !registrationComplete && step === 3 ? '560px' : '440px' }}>
        {/* Language switcher */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
          <LanguageSwitcher />
        </div>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <Link href="/" style={{ textDecoration: 'none' }}>
            <span style={{ fontSize: '24px', fontWeight: 800, color: '#fff', letterSpacing: '-0.5px' }}>
              Rezervo <span style={{ color: '#7C3AED' }}>Online</span>
            </span>
          </Link>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', marginTop: '8px' }}>{tr.tagline}</p>
        </div>

        {/* Step indicator */}
        {!registrationComplete && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '24px' }}>
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                  <div style={{
                    width: '28px', height: '28px', borderRadius: '50%',
                    background: step >= s ? 'linear-gradient(135deg, #7C3AED, #5D0AF4)' : 'rgba(255,255,255,0.1)',
                    border: step >= s ? 'none' : '1px solid rgba(255,255,255,0.2)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '12px', fontWeight: 700, color: step >= s ? '#fff' : 'rgba(255,255,255,0.4)',
                  }}>
                    {step > s ? '✓' : s}
                  </div>
                  <span style={{ fontSize: '10px', color: step >= s ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.25)', fontWeight: 500 }}>{tr.stepLabels[s - 1]}</span>
                </div>
                {s < 3 && <div style={{ width: '40px', height: '1px', background: step > s ? 'rgba(124,58,237,0.6)' : 'rgba(255,255,255,0.1)', marginBottom: '16px' }} />}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Card */}
        <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '20px', padding: '40px', backdropFilter: 'blur(20px)' }}>

          {/* ─── REGISTRATION SUCCESS — install is optional, dashboard is already reachable ─── */}
          {registrationComplete && (
            <div style={{ textAlign: 'center' }}>
              <h1 style={{ color: '#fff', fontSize: '22px', fontWeight: 700, marginBottom: '10px' }}>{t.pwa.registerSuccess.heading}</h1>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14.5px', marginBottom: '28px', lineHeight: 1.5 }}>{t.pwa.registerSuccess.body}</p>

              {pwaMounted && isIOS && (
                <button
                  onClick={() => setShowIosSheet(true)}
                  style={{ width: '100%', background: 'linear-gradient(135deg, #7C3AED, #5D0AF4)', border: 'none', borderRadius: '12px', padding: '14px', color: '#fff', fontSize: '15.5px', fontWeight: 600, cursor: 'pointer', marginBottom: '12px' }}
                >
                  {t.pwa.dashboardCard.iosCta}
                </button>
              )}
              {pwaMounted && !isIOS && isAndroid && canInstallAndroid && (
                <button
                  onClick={() => promptInstall()}
                  style={{ width: '100%', background: 'linear-gradient(135deg, #7C3AED, #5D0AF4)', border: 'none', borderRadius: '12px', padding: '14px', color: '#fff', fontSize: '15.5px', fontWeight: 600, cursor: 'pointer', marginBottom: '12px' }}
                >
                  {t.pwa.dashboardCard.androidCta}
                </button>
              )}

              <button
                onClick={() => router.push('/dashboard')}
                style={{ width: '100%', background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '12px', padding: '13px', color: 'rgba(255,255,255,0.75)', fontSize: '14.5px', fontWeight: 600, cursor: 'pointer' }}
              >
                {t.pwa.registerSuccess.continueCta}
              </button>
            </div>
          )}

          {/* ─── STEP 1: Account ─── */}
          {!registrationComplete && step === 1 && (
            <>
              <h1 style={{ color: '#fff', fontSize: '22px', fontWeight: 700, marginBottom: '8px', textAlign: 'center' }}>{tr.step1.title}</h1>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', textAlign: 'center', marginBottom: '28px' }}>{tr.step1.subtitle}</p>
              <form onSubmit={handleStep1}>
                {[
                  { label: tr.step1.businessNameLabel, key: 'businessName', type: 'text', placeholder: tr.step1.businessNamePlaceholder },
                  { label: tr.step1.emailLabel, key: 'email', type: 'email', placeholder: tr.step1.emailPlaceholder },
                  { label: tr.step1.phoneLabel, key: 'phone', type: 'tel', placeholder: tr.step1.phonePlaceholder },
                  { label: tr.step1.passwordLabel, key: 'password', type: 'password', placeholder: tr.step1.passwordPlaceholder },
                ].map(({ label, key, type, placeholder }) => (
                  <div key={key} style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '14px', marginBottom: '8px' }}>{label}</label>
                    <input
                      type={type}
                      value={form[key as keyof typeof form]}
                      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                      placeholder={placeholder}
                      required={key !== 'phone'}
                      style={inputStyle}
                    />
                  </div>
                ))}
                {error && <div style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', color: '#fca5a5', fontSize: '14px' }}>{error}</div>}
                <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '12px', lineHeight: 1.5, marginBottom: '12px' }}>
                  {t.legal.registerNotice.prefix}
                  <Link href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(255,255,255,0.65)', textDecoration: 'underline' }}>
                    {t.legal.registerNotice.termsLink}
                  </Link>
                  {t.legal.registerNotice.middle}
                  <Link href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(255,255,255,0.65)', textDecoration: 'underline' }}>
                    {t.legal.registerNotice.privacyLink}
                  </Link>
                  {t.legal.registerNotice.suffix}
                </p>
                <button type="submit" disabled={loading}
                  style={{ width: '100%', background: loading ? 'rgba(124,58,237,0.5)' : 'linear-gradient(135deg, #7C3AED, #5D0AF4)', border: 'none', borderRadius: '12px', padding: '14px', color: '#fff', fontSize: '16px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', marginTop: '8px' }}>
                  {loading ? tr.step1.creatingAccountCta : tr.continueCta}
                </button>
              </form>
              <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: '14px', marginTop: '24px' }}>
                {tr.alreadyHaveAccount}{' '}
                <Link href="/login" style={{ color: '#a78bfa', textDecoration: 'none', fontWeight: 600 }}>{tr.loginHere}</Link>
              </p>
            </>
          )}

          {/* ─── STEP 2: Business Info ─── */}
          {!registrationComplete && step === 2 && (
            <>
              <h1 style={{ color: '#fff', fontSize: '22px', fontWeight: 700, marginBottom: '8px', textAlign: 'center' }}>{tr.step2.title}</h1>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', textAlign: 'center', marginBottom: '28px' }}>{tr.step2.subtitle}</p>
              <form onSubmit={handleStep2}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '14px', marginBottom: '8px' }}>{tr.step2.categoryLabel}</label>
                  <select value={onboarding.category} onChange={(e) => setOnboarding({ ...onboarding, category: e.target.value })} required
                    style={{ ...inputStyle, appearance: 'none' }}>
                    <option value="" style={{ background: '#1a0035' }}>{tr.step2.categoryPlaceholder}</option>
                    {CATEGORIES.map((cat) => (
                      <option key={cat.value} value={cat.value} style={{ background: '#1a0035' }}>{resolveCategoryLabel(cat.value, t.dashboard.categories)}</option>
                    ))}
                  </select>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '14px', marginBottom: '8px' }}>{tr.step2.cityLabel}</label>
                  <input type="text" value={onboarding.city} onChange={(e) => setOnboarding({ ...onboarding, city: e.target.value })} placeholder={tr.step2.cityPlaceholder} required style={inputStyle} />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '14px', marginBottom: '8px' }}>{tr.step2.addressLabel}</label>
                  <input type="text" value={onboarding.address} onChange={(e) => setOnboarding({ ...onboarding, address: e.target.value })} placeholder={tr.step2.addressPlaceholder} style={inputStyle} />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '14px', marginBottom: '8px' }}>{tr.step2.descriptionLabel}</label>
                  <textarea value={onboarding.description} onChange={(e) => setOnboarding({ ...onboarding, description: e.target.value })} placeholder={tr.step2.descriptionPlaceholder} rows={3}
                    style={{ ...inputStyle, resize: 'vertical', minHeight: '80px' }} />
                </div>
                {error && <div style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', color: '#fca5a5', fontSize: '14px' }}>{error}</div>}
                <button type="submit" disabled={loading}
                  style={{ width: '100%', background: loading ? 'rgba(124,58,237,0.5)' : 'linear-gradient(135deg, #7C3AED, #5D0AF4)', border: 'none', borderRadius: '12px', padding: '14px', color: '#fff', fontSize: '16px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', marginTop: '8px' }}>
                  {loading ? tr.savingCta : tr.continueCta}
                </button>
              </form>
            </>
          )}

          {/* ─── STEP 3: Working Hours + Booking Interval ─── */}
          {!registrationComplete && step === 3 && (
            <>
              <h1 style={{ color: '#fff', fontSize: '22px', fontWeight: 700, marginBottom: '8px', textAlign: 'center' }}>{tr.step3.title}</h1>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', textAlign: 'center', marginBottom: '28px' }}>{tr.step3.subtitle}</p>
              <form onSubmit={handleStep3}>
                {/* Booking interval */}
                <div style={{ marginBottom: '24px', background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.2)', borderRadius: '12px', padding: '16px' }}>
                  <label style={{ display: 'block', color: 'rgba(255,255,255,0.8)', fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>
                    {tr.step3.bookingIntervalLabel}
                  </label>
                  <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', marginBottom: '12px' }}>
                    {tr.step3.bookingIntervalHelp}
                  </p>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {([15, 30, 60] as const).map((min) => (
                      <button key={min} type="button" onClick={() => setBookingInterval(min)}
                        style={{ flex: 1, padding: '10px', borderRadius: '10px', border: bookingInterval === min ? '2px solid #7C3AED' : '1px solid rgba(255,255,255,0.15)', background: bookingInterval === min ? 'rgba(124,58,237,0.25)' : 'rgba(255,255,255,0.05)', color: bookingInterval === min ? '#c4b5fd' : 'rgba(255,255,255,0.6)', fontSize: '14px', fontWeight: bookingInterval === min ? 700 : 400, cursor: 'pointer', transition: 'all 0.15s' }}>
                        {min} min
                      </button>
                    ))}
                  </div>
                </div>

                {/* Weekly schedule */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', color: 'rgba(255,255,255,0.8)', fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>
                    {tr.step3.weeklyScheduleLabel}
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {DAY_KEYS.map((key) => {
                      const day = schedule[key];
                      return (
                        <div key={key} className="hours-row" style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)' }}>
                          {/* Toggle */}
                          <button type="button" onClick={() => setSchedule({ ...schedule, [key]: { ...day, is_open: !day.is_open } })}
                            style={{ width: '40px', height: '22px', borderRadius: '11px', border: 'none', background: day.is_open ? '#7C3AED' : 'rgba(255,255,255,0.1)', cursor: 'pointer', position: 'relative', flexShrink: 0, transition: 'background 0.2s' }}>
                            <span style={{ position: 'absolute', top: '3px', left: day.is_open ? '21px' : '3px', width: '16px', height: '16px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s', display: 'block' }} />
                          </button>
                          {/* Day name */}
                          <span className="hours-day-label" style={{ color: day.is_open ? '#fff' : 'rgba(255,255,255,0.35)', fontSize: '13px', fontWeight: 500 }}>{t.dashboard.days[key]}</span>
                          {day.is_open ? (
                            <div className="hours-time-group">
                              <input type="time" value={day.open_time} onChange={(e) => setSchedule({ ...schedule, [key]: { ...day, open_time: e.target.value } })}
                                style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '6px 10px', color: '#fff', fontSize: '13px' }} />
                              <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>—</span>
                              <input type="time" value={day.close_time} onChange={(e) => setSchedule({ ...schedule, [key]: { ...day, close_time: e.target.value } })}
                                style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '6px 10px', color: '#fff', fontSize: '13px' }} />
                            </div>
                          ) : (
                            <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: '13px', fontStyle: 'italic' }}>{tr.step3.closedLabel}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {error && <div style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', color: '#fca5a5', fontSize: '14px' }}>{error}</div>}
                <button type="submit" disabled={loading}
                  style={{ width: '100%', background: loading ? 'rgba(124,58,237,0.5)' : 'linear-gradient(135deg, #7C3AED, #5D0AF4)', border: 'none', borderRadius: '12px', padding: '14px', color: '#fff', fontSize: '16px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', marginTop: '8px' }}>
                  {loading ? tr.savingCta : tr.step3.finishCta}
                </button>
              </form>
            </>
          )}
        </div>

        <p style={{ textAlign: 'center', marginTop: '24px' }}>
          <Link href="/" style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', textDecoration: 'none' }}>
            ← {t.booking.backToHome}
          </Link>
        </p>
      </div>

      {showIosSheet && (
        <IosInstallSheet isDark onClose={() => setShowIosSheet(false)} />
      )}
    </div>
  );
}
